import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  ClipboardCheck,
  MessageSquarePlus,
  X,
  Loader2,
  Bus,
  User,
  MapPin,
  Calendar,
  FileText,
} from "lucide-react";
import {
  PortalPageShell,
  PortalPrimaryBtn,
  PortalGhostBtn,
  PortalOutlineBtn,
  PortalStatusChip,
  fmtDate,
} from "@/pages/portal/PortalShared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem("aa_portal_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ─── Format "YYYY-MM-DD HH:MM" → "Sep 12, 2026 · 8:00 AM" ─── */
const fmtDT = (v) => {
  if (!v) return "—";
  try {
    const [datePart, timePart] = v.split(" ");
    if (!datePart) return v;
    const d = new Date(datePart + "T" + (timePart || "00:00"));
    if (isNaN(d.getTime())) return v;
    const dateStr = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    if (!timePart) return dateStr;
    const timeStr = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${dateStr} · ${timeStr}`;
  } catch {
    return v;
  }
};

/* ─── Field display — label + value ─── */
const Field = ({ label, value, testid, mono }) => (
  <div data-testid={testid}>
    <dt
      className="text-[10.5px] uppercase tracking-[0.12em] font-semibold"
      style={{ color: "var(--fg-muted)" }}
    >
      {label}
    </dt>
    <dd
      className={`mt-1 text-[14px] break-words ${mono ? "font-mono" : ""}`}
      style={{ color: value ? "var(--fg-primary)" : "var(--fg-muted)" }}
    >
      {value || "—"}
    </dd>
  </div>
);

/* ─── Section card wrapper ─── */
const ReviewSection = ({ icon: Icon, label, children, testid }) => (
  <section
    className="rounded-md p-5 md:p-6"
    style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border-default)",
    }}
    data-testid={testid}
  >
    <div
      className="uppercase text-[10.5px] tracking-[0.14em] font-semibold mb-4 flex items-center gap-2"
      style={{ color: "var(--fg-muted)" }}
    >
      {Icon && <Icon size={14} />}
      {label}
    </div>
    {children}
  </section>
);

/* ─── Request Changes Dialog ─── */
const ChangesDialog = ({
  open,
  onClose,
  comment,
  setComment,
  onSubmit,
  submitting,
}) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="pickup-changes-dialog"
    >
      {/* Backdrop */}
      <button
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={onClose}
        aria-label="Close dialog"
        tabIndex={-1}
        data-testid="pickup-changes-backdrop"
      />
      {/* Panel */}
      <div
        className="relative w-full max-w-lg rounded-md p-6"
        style={{
          background: "var(--bg-elevated)",
          border: "1px solid var(--border-default)",
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Request Changes"
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-[18px] font-semibold"
            style={{ color: "var(--fg-primary)" }}
          >
            Request Changes
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-md transition-colors"
            style={{ color: "var(--fg-muted)" }}
            data-testid="pickup-changes-close-btn"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p
          className="text-[13.5px] mb-3"
          style={{ color: "var(--fg-secondary)" }}
        >
          Describe what needs to be changed on the pickup sheet. Your feedback
          will be sent to the All Access operations team.
        </p>

        <label
          htmlFor="pickup-changes-comment"
          className="text-[11px] uppercase tracking-[0.12em] font-semibold"
          style={{ color: "var(--fg-muted)" }}
        >
          Your Comments *
        </label>
        <textarea
          id="pickup-changes-comment"
          ref={textareaRef}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Please describe what needs to be changed…"
          rows={4}
          className="mt-1 w-full rounded-md p-3 text-[14px] resize-none focus:outline-none focus:ring-2 focus:ring-[#1B4F8A]"
          style={{
            background: "var(--bg-inset)",
            color: "var(--fg-primary)",
            border: "1px solid var(--border-default)",
          }}
          data-testid="pickup-changes-comment"
        />

        <div className="mt-4 flex items-center justify-end gap-3">
          <PortalGhostBtn
            onClick={onClose}
            disabled={submitting}
            data-testid="pickup-changes-cancel-btn"
          >
            Cancel
          </PortalGhostBtn>
          <PortalPrimaryBtn
            onClick={onSubmit}
            disabled={submitting || !comment.trim()}
            data-testid="pickup-changes-submit-btn"
            style={{
              opacity: submitting || !comment.trim() ? 0.5 : 1,
              cursor:
                submitting || !comment.trim() ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Submitting…" : "Submit Feedback"}
          </PortalPrimaryBtn>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────────────────── */
/*  Main Component                                                   */
/* ───────────────────────────────────────────────────────────────── */

export default function PortalPickupReview() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tourId = searchParams.get("tour");
  const daId = searchParams.get("da");

  /* ── Data state ── */
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ── Action state ── */
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [submittingChanges, setSubmittingChanges] = useState(false);
  const [changesSubmitted, setChangesSubmitted] = useState(false);
  const [actionError, setActionError] = useState(null);

  /* ── Fetch ── */
  const fetchReview = useCallback(async () => {
    if (!tourId || !daId) {
      setError(
        "Missing tour or driver assignment parameters. Please use the link provided by All Access."
      );
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BACKEND}/api/portal/tours/${tourId}/pickup-sheet/${daId}/review`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        if (res.status === 404)
          throw new Error("Pickup sheet not found for this tour.");
        if (res.status === 403)
          throw new Error(
            "Your session has expired. Please request a fresh portal link from your Account Manager."
          );
        throw new Error(
          `Unable to load pickup sheet (Error ${res.status}).`
        );
      }
      const json = await res.json();
      setData(json);
      if (json.client_current) {
        setApproved(true);
      }
      // Cache for cross-link discovery on Documents page
      try {
        localStorage.setItem(
          "aa_portal_pickup_review",
          JSON.stringify({
            tourId,
            daId,
            clientName: json.snapshot?.client_name,
            coachName: json.snapshot?.coach_name,
            visitedAt: new Date().toISOString(),
          })
        );
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tourId, daId]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  /* ── Approve ── */
  const handleApprove = async () => {
    if (approving || approved) return;
    setApproving(true);
    setActionError(null);
    try {
      const res = await fetch(
        `${BACKEND}/api/portal/tours/${tourId}/pickup-sheet/${daId}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({}),
        }
      );
      if (!res.ok) {
        if (res.status === 409)
          throw new Error(
            "The pickup sheet has been updated since you loaded it. Please refresh the page."
          );
        if (res.status === 403)
          throw new Error("Your session has expired.");
        throw new Error(`Approval failed (Error ${res.status}).`);
      }
      setApproved(true);
    } catch (e) {
      setActionError(e.message);
    } finally {
      setApproving(false);
    }
  };

  /* ── Request Changes ── */
  const handleRequestChanges = async () => {
    if (submittingChanges || !comment.trim()) return;
    setSubmittingChanges(true);
    setActionError(null);
    try {
      const res = await fetch(
        `${BACKEND}/api/portal/tours/${tourId}/pickup-sheet/${daId}/request-changes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ comment: comment.trim() }),
        }
      );
      if (!res.ok) {
        if (res.status === 422)
          throw new Error(
            "Please provide a comment describing the changes needed."
          );
        if (res.status === 403) throw new Error("Your session has expired.");
        throw new Error(`Request failed (Error ${res.status}).`);
      }
      setChangesSubmitted(true);
      setChangesOpen(false);
      setComment("");
    } catch (e) {
      setActionError(e.message);
    } finally {
      setSubmittingChanges(false);
    }
  };

  /* ══════════════════ Render ══════════════════ */

  /* Loading */
  if (loading) {
    return (
      <div
        className="max-w-[880px] mx-auto"
        data-testid="page-portal-pickup-review"
      >
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2
              size={24}
              className="animate-spin mx-auto"
              style={{ color: "var(--fg-muted)" }}
            />
            <div
              className="mt-3 text-[13.5px]"
              style={{ color: "var(--fg-muted)" }}
            >
              Loading pickup sheet review…
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* Error */
  if (error) {
    return (
      <div
        className="max-w-[880px] mx-auto"
        data-testid="page-portal-pickup-review"
      >
        <PortalPageShell eyebrow="Pickup Sheet" title="Review Unavailable">
          <div
            className="rounded-md p-6 flex items-start gap-3"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
            }}
            data-testid="pickup-review-error"
          >
            <AlertTriangle
              size={20}
              className="shrink-0 mt-0.5"
              style={{ color: "#D79A27" }}
            />
            <div>
              <div
                className="text-[14px] font-semibold"
                style={{ color: "var(--fg-primary)" }}
              >
                Unable to load review
              </div>
              <div
                className="mt-1 text-[13.5px]"
                style={{ color: "var(--fg-secondary)" }}
              >
                {error}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <PortalGhostBtn
              onClick={() => navigate("/portal/overview")}
              data-testid="pickup-review-back-overview"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Overview
            </PortalGhostBtn>
          </div>
        </PortalPageShell>
      </div>
    );
  }

  const snap = data?.snapshot;
  if (!snap) return null;

  const lastApproval = data?.last_approval;
  const isAlreadyApproved = approved || data?.client_current;
  const hasLastChanges =
    lastApproval?.status === "changes_requested" && !isAlreadyApproved;

  /* Status badge */
  let statusTone = "hold";
  let statusLabel = "Pending Review";
  if (isAlreadyApproved) {
    statusTone = "lime";
    statusLabel = "Client Approved";
  } else if (changesSubmitted) {
    statusTone = "blue";
    statusLabel = "Changes Requested";
  } else if (hasLastChanges) {
    statusTone = "hold";
    statusLabel = "Under Revision";
  }

  return (
    <div
      className="max-w-[880px] mx-auto"
      data-testid="page-portal-pickup-review"
    >
      <PortalPageShell
        eyebrow="Pickup Sheet"
        title="Pickup Sheet Review"
        subtitle="Review the details below and approve, or request changes."
        right={
          <PortalStatusChip
            tone={statusTone}
            testid="pickup-review-status"
          >
            {statusLabel}
          </PortalStatusChip>
        }
      >
        {/* ── Action error banner ── */}
        {actionError && (
          <div
            className="rounded-md p-4 mb-6 flex items-start gap-3"
            style={{
              background: "rgba(184,60,60,0.06)",
              border: "1px solid rgba(184,60,60,0.2)",
            }}
            data-testid="pickup-review-action-error"
          >
            <AlertTriangle
              size={16}
              className="shrink-0 mt-0.5"
              style={{ color: "#B83C3C" }}
            />
            <div className="text-[13.5px]" style={{ color: "#B83C3C" }}>
              {actionError}
            </div>
          </div>
        )}

        {/* ── Approved banner ── */}
        {isAlreadyApproved && (
          <div
            className="rounded-md p-5 mb-6 flex items-start gap-3"
            style={{
              background: "rgba(183,227,29,0.08)",
              borderLeft: "3px solid #B7E31D",
            }}
            data-testid="pickup-review-approved-banner"
          >
            <CheckCircle2
              size={20}
              className="shrink-0 mt-0.5"
              style={{ color: "#B7E31D" }}
            />
            <div>
              <div
                className="text-[15px] font-semibold"
                style={{ color: "var(--fg-primary)" }}
                data-testid="pickup-review-approved-heading"
              >
                Client Approved
              </div>
              <div
                className="mt-1 text-[13.5px]"
                style={{ color: "var(--fg-secondary)" }}
              >
                {lastApproval?.approved_at
                  ? `Approved on ${fmtDate(lastApproval.approved_at)}`
                  : "This pickup sheet has been approved."}
              </div>
            </div>
          </div>
        )}

        {/* ── Changes submitted banner ── */}
        {changesSubmitted && !isAlreadyApproved && (
          <div
            className="rounded-md p-5 mb-6 flex items-start gap-3"
            style={{
              background: "rgba(27,79,138,0.06)",
              borderLeft: "3px solid #1B4F8A",
            }}
            data-testid="pickup-review-changes-banner"
          >
            <MessageSquarePlus
              size={20}
              className="shrink-0 mt-0.5"
              style={{ color: "#1B4F8A" }}
            />
            <div>
              <div
                className="text-[15px] font-semibold"
                style={{ color: "var(--fg-primary)" }}
              >
                Changes Requested
              </div>
              <div
                className="mt-1 text-[13.5px]"
                style={{ color: "var(--fg-secondary)" }}
              >
                Your feedback has been sent to All Access. They&apos;ll update the
                pickup sheet and you&apos;ll receive a new review link.
              </div>
            </div>
          </div>
        )}

        {/* ── Previous change request banner ── */}
        {hasLastChanges && !changesSubmitted && (
          <div
            className="rounded-md p-5 mb-6 flex items-start gap-3"
            style={{
              background: "rgba(215,154,39,0.06)",
              borderLeft: "3px solid #D79A27",
            }}
            data-testid="pickup-review-revision-banner"
          >
            <AlertTriangle
              size={20}
              className="shrink-0 mt-0.5"
              style={{ color: "#D79A27" }}
            />
            <div>
              <div
                className="text-[15px] font-semibold"
                style={{ color: "var(--fg-primary)" }}
              >
                Under Revision
              </div>
              <div
                className="mt-1 text-[13.5px]"
                style={{ color: "var(--fg-secondary)" }}
              >
                Changes were previously requested
                {lastApproval?.comment && (
                  <span>
                    : &ldquo;<em>{lastApproval.comment}</em>&rdquo;
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══════ Snapshot Sections ═══════ */}
        <div className="space-y-4">
          {/* Tour & Equipment */}
          <ReviewSection
            icon={Bus}
            label="Tour & Equipment"
            testid="pickup-review-section-tour"
          >
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
              <Field
                label="Client"
                value={snap.client_name}
                testid="pickup-field-client-name"
              />
              <Field
                label="Coach"
                value={snap.coach_name}
                testid="pickup-field-coach-name"
              />
              <Field
                label="Trailer"
                value={snap.trailer_name}
                testid="pickup-field-trailer-name"
              />
              <Field
                label="Passengers"
                value={
                  snap.passengers != null ? String(snap.passengers) : null
                }
                testid="pickup-field-passengers"
              />
              <Field
                label="Number of Bunks"
                value={
                  snap.minimum_bunks != null
                    ? String(snap.minimum_bunks)
                    : null
                }
                testid="pickup-field-bunks"
              />
            </dl>
          </ReviewSection>

          {/* Driver */}
          <ReviewSection
            icon={User}
            label="Driver"
            testid="pickup-review-section-driver"
          >
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
              <Field
                label="Driver Name"
                value={snap.driver_name}
                testid="pickup-field-driver-name"
              />
            </dl>
          </ReviewSection>

          {/* Pickup Details */}
          <ReviewSection
            icon={MapPin}
            label="Pickup Details"
            testid="pickup-review-section-pickup"
          >
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
              <Field
                label="Pickup Date & Time"
                value={fmtDT(snap.pickup_datetime)}
                testid="pickup-field-pickup-dt"
              />
              <Field
                label="Depart Date & Time"
                value={fmtDT(snap.depart_datetime)}
                testid="pickup-field-depart-dt"
              />
              <Field
                label="Pickup Location"
                value={snap.pickup_location || snap.pickup_address}
                testid="pickup-field-pickup-location"
              />
              <Field
                label="Pickup Address"
                value={snap.pickup_address}
                testid="pickup-field-pickup-address"
              />
              <Field
                label="Hotel Room at Pickup"
                value={snap.hotel_room_at_pickup}
                testid="pickup-field-hotel"
              />
              <Field
                label="Pickup Contact"
                value={snap.pickup_contact_name}
                testid="pickup-field-contact-name"
              />
              <Field
                label="Contact Phone"
                value={snap.pickup_contact_phone}
                testid="pickup-field-contact-phone"
              />
            </dl>
          </ReviewSection>

          {/* Ending Details */}
          <ReviewSection
            icon={Calendar}
            label="Ending Details"
            testid="pickup-review-section-ending"
          >
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
              <Field
                label="Ending Date & Time"
                value={fmtDT(snap.ending_datetime)}
                testid="pickup-field-ending-dt"
              />
              <Field
                label="Ending Address"
                value={snap.ending_address}
                testid="pickup-field-ending-address"
              />
            </dl>
          </ReviewSection>

          {/* Special Instructions (client-safe ONLY) */}
          {snap.client_safe_instructions && (
            <ReviewSection
              icon={FileText}
              label="Special Instructions"
              testid="pickup-review-section-instructions"
            >
              <div
                className="text-[14px] whitespace-pre-wrap break-words leading-relaxed"
                style={{ color: "var(--fg-primary)" }}
                data-testid="pickup-field-instructions"
              >
                {snap.client_safe_instructions}
              </div>
            </ReviewSection>
          )}
        </div>

        {/* ═══════ Action Buttons ═══════ */}
        {!isAlreadyApproved && !changesSubmitted && (
          <div
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            data-testid="pickup-review-actions"
          >
            <PortalPrimaryBtn
              onClick={handleApprove}
              disabled={approving}
              data-testid="pickup-approve-btn"
              style={{ opacity: approving ? 0.6 : 1 }}
            >
              <ClipboardCheck size={16} className="mr-2" />
              {approving ? "Approving…" : "Approve Pickup Sheet"}
            </PortalPrimaryBtn>
            <PortalOutlineBtn
              onClick={() => setChangesOpen(true)}
              data-testid="pickup-request-changes-btn"
            >
              <MessageSquarePlus size={16} className="mr-2" />
              Request Changes
            </PortalOutlineBtn>
          </div>
        )}

        {/* Back link */}
        <div className="mt-6 pb-4">
          <PortalGhostBtn
            onClick={() => navigate("/portal/overview")}
            data-testid="pickup-review-back"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Overview
          </PortalGhostBtn>
        </div>
      </PortalPageShell>

      {/* Request Changes Dialog */}
      <ChangesDialog
        open={changesOpen}
        onClose={() => setChangesOpen(false)}
        comment={comment}
        setComment={setComment}
        onSubmit={handleRequestChanges}
        submitting={submittingChanges}
      />
    </div>
  );
}
